import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Download, TrendingUp, Users, Coins, Percent, CalendarDays, FileEdit } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const zar = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const zarShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return `R${(v/1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `R${(v/1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `R${(v/1e3).toFixed(1)}k`;
  return `R${v.toFixed(0)}`;
};

const PIE_COLORS = ['#18A37C', '#1D2D2A', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#84CC16'];

export default function IRDashboardPage() {
  const { token, user } = useAuth();
  const [scope, setScope] = useState('all');
  const [period, setPeriod] = useState('1Y');
  const [metrics, setMetrics] = useState(null);
  const [nav, setNav] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      setLoading(true);
      const [m, n] = await Promise.all([
        axios.get(`${API_URL}/api/ir/portfolio-metrics?scope=${scope}`, authHeader),
        axios.get(`${API_URL}/api/ir/nav-timeseries?scope=${scope}&period=${period}`, authHeader),
      ]);
      setMetrics(m.data);
      setNav(n.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load metrics');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, period, token]);

  const exportFactsheet = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API_URL}/api/ir/factsheet-pdf?scope=${scope}`, authHeader);
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(r.data.html);
        w.document.close();
        setTimeout(() => { try { w.print(); } catch (_) { /* no-op */ } }, 500);
      } else {
        toast.error('Pop-up blocked. Please allow pop-ups to export the factsheet.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to export');
    } finally { setExporting(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="ir-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1D2D2A] font-['Manrope']">Investor Relations · Portfolio</h1>
          <p className="text-[#6C757D] mt-1">
            Signed in as <span className="font-medium">{user?.email}</span> · Investor <span className="font-mono">05-00</span> is always excluded from portfolio metrics.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-56" data-testid="ir-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All investors</SelectItem>
              <SelectItem value="discretionary">Discretionary mandates</SelectItem>
              <SelectItem value="non_discretionary">Non-discretionary mandates</SelectItem>
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32" data-testid="ir-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="6M">6 Months</SelectItem>
              <SelectItem value="1Y">1 Year</SelectItem>
              <SelectItem value="2Y">2 Years</SelectItem>
              <SelectItem value="ALL">All-time</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportFactsheet} disabled={exporting || loading} data-testid="ir-export" className="bg-[#18A37C] hover:bg-[#1D2D2A] text-white">
            {exporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing…</> : <><Download className="h-4 w-4 mr-2" /> Export factsheet</>}
          </Button>
          <TemplateEditor authHeader={authHeader} />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#6C757D]"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading metrics…</div>
      ) : !metrics ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Tile icon={<Coins className="h-5 w-5 text-[#18A37C]" />} label="Total portfolio" value={zar(metrics.total_portfolio)} />
            <Tile icon={<TrendingUp className="h-5 w-5 text-[#18A37C]" />} label="Capital deployed" value={zar(metrics.total_capital_deployed)} />
            <Tile icon={<Coins className="h-5 w-5 text-[#18A37C]" />} label="Cash balance" value={zar(metrics.total_cash_balance)} />
            <Tile icon={<Percent className="h-5 w-5 text-[#18A37C]" />} label="Weighted yield" value={`${metrics.weighted_avg_yield}%`} />
            <Tile icon={<Users className="h-5 w-5 text-[#18A37C]" />} label="Investors" value={metrics.investor_count} />
            <Tile icon={<CalendarDays className="h-5 w-5 text-[#18A37C]" />} label="Avg tenor" value={`${metrics.avg_tenor_remaining_days} days`} />
            <Tile label="Active loans" value={metrics.active_loan_count} />
            <Tile label="Discretionary" value={`${metrics.mandate_breakdown?.discretionary || 0} / ${metrics.investor_count}`} />
          </div>

          {/* NAV vs Benchmark */}
          <Card>
            <CardHeader>
              <CardTitle>NAV vs Benchmark ({period})</CardTitle>
              <CardDescription>
                Aggregate portfolio NAV over the selected period vs Prime rate benchmark ({nav?.summary?.benchmark_rate}% p.a.).
                Total return {nav?.summary?.total_return}% · {zar(nav?.summary?.start_value)} → {zar(nav?.summary?.end_value)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(nav?.nav_series?.length || 0) === 0 ? (
                <div className="py-10 text-center text-[#6C757D]">No NAV series data yet for this scope + period.</div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={nav.nav_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={zarShort} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => zar(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="portfolio_value" name="Portfolio NAV" stroke="#18A37C" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="benchmark_index" name="Benchmark (Prime)" stroke="#6C757D" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Monthly performance matrix (Jan-Dec per year + YTD annualised) */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly performance</CardTitle>
              <CardDescription>Jan-Dec per year, plus YTD and YTD annualised for partial years. Same math as each investor&apos;s Reports page.</CardDescription>
            </CardHeader>
            <CardContent>
              {(nav?.monthly_matrix?.length || 0) === 0 ? (
                <div className="py-6 text-center text-[#6C757D]">No monthly data yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Year</TableHead>
                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(mn => (
                          <TableHead key={mn} className="text-right">{mn}</TableHead>
                        ))}
                        <TableHead className="text-right">YTD</TableHead>
                        <TableHead className="text-right">YTD (Ann.)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {nav.monthly_matrix.map(row => (
                        <TableRow key={row.year} data-testid={`ir-year-${row.year}`}>
                          <TableCell className="font-bold">{row.year}</TableCell>
                          {row.months.map((v, i) => (
                            <TableCell key={i} className={`text-right font-mono text-xs ${v == null ? 'text-[#6C757D]' : v >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {v == null ? '—' : `${v}%`}
                            </TableCell>
                          ))}
                          <TableCell className={`text-right font-mono ${row.ytd >= 0 ? 'text-green-700' : 'text-red-700'}`}>{row.ytd}%</TableCell>
                          <TableCell className={`text-right font-mono ${row.ytd_annualised >= 0 ? 'text-green-700' : 'text-red-700'}`}>{row.full_year ? row.ytd_annualised + '%' : `${row.ytd_annualised}%*`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="text-xs text-[#6C757D] mt-2">* Partial year: YTD compounded return annualised as (1 + YTD) ^ (12/months) − 1.</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sector + Tenor pies */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Sector exposure</CardTitle><CardDescription>Close-of-period exposure by sector.</CardDescription></CardHeader>
              <CardContent>
                <ExposurePie rows={metrics.exposure_by_sector} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Tenor remaining</CardTitle><CardDescription>Days-to-maturity distribution.</CardDescription></CardHeader>
              <CardContent>
                <ExposurePie rows={metrics.exposure_by_tenor} />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Included investors ({metrics.investors.length})</CardTitle>
              <CardDescription>Read-only view. Investor 05-00 is excluded from the metrics above.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>RP</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Mandate</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {metrics.investors.map(i => (
                      <TableRow key={i.id} data-testid={`ir-investor-${i.id}`}>
                        <TableCell className="font-mono text-xs">{i.rp_code}</TableCell>
                        <TableCell>{i.name || '—'}</TableCell>
                        <TableCell className="text-xs">{i.email}</TableCell>
                        <TableCell className="text-xs">{i.mandate_type || <span className="text-[#6C757D] italic">unset</span>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Tile({ icon, label, value }) {
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-[#6C757D] uppercase tracking-wide">{label}</span>
        {icon}
      </div>
      <div className="mt-2 text-xl font-bold text-[#1D2D2A]">{value}</div>
    </div>
  );
}

function ExposurePie({ rows }) {
  const entries = Object.entries(rows || {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <div className="py-10 text-center text-[#6C757D]">No data.</div>;
  const data = entries.map(([name, value]) => ({ name, value }));
  const total = data.reduce((a, b) => a + b.value, 0);
  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => `${((e.value / total) * 100).toFixed(1)}%`}>
          {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(v) => zar(v)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function TemplateEditor({ authHeader }) {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/ir/factsheet-template`, authHeader);
      setHtml(r.data.html_content || '');
      setPlaceholders(r.data.placeholders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load template');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/ir/factsheet-template`, { html_content: html }, authHeader);
      toast.success('Template saved');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="ir-edit-template">
        <FileEdit className="h-4 w-4 mr-2" /> Edit template
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Edit factsheet template</DialogTitle>
            <DialogDescription>
              HTML template used to render the exported factsheet. Placeholders (Python .format style): {placeholders.map(p => <code key={p} className="mx-1 text-xs">{`{${p}}`}</code>)}
            </DialogDescription>
          </DialogHeader>
          {loading ? <div className="py-10 text-center"><Loader2 className="inline h-6 w-6 animate-spin" /></div>
           : <Textarea value={html} onChange={e => setHtml(e.target.value)} rows={20} className="font-mono text-xs" data-testid="ir-template-html" />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="ir-template-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save template'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

